import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { 
  Phone, Mail, MapPin, Award, Clock, Users, Truck, Package, 
  Shield, TrendingUp, Leaf, CheckCircle, ArrowRight, Star, Menu, X, 
  ChevronDown, Sparkles, Zap, Building2, Loader2, Send
} from 'lucide-react';
import { mockData } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Animated Image Gallery Component
const ImageGallerySection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const galleryImages = [
    { url: '/gallery_teja_1.jpg', title: 'Teja S17', subtitle: 'Premium Heat Variety' },
    { url: '/gallery_byadgi_1.jpg', title: 'Byadgi', subtitle: 'Premium Color Variety' },
    { url: '/gallery_sanam_1.jpg', title: 'Sanam S334', subtitle: 'Medium Heat Variety' },
    { url: '/gallery_endo_1.jpg', title: 'Endo 5', subtitle: 'Premium Blood Red' },
    { url: '/gallery_armour_1.jpg', title: 'Armour S18', subtitle: 'Premium Heat Variety' },
    { url: '/gallery_shark_1.jpg', title: 'Shark S21', subtitle: 'Teja Alternative' },
    { url: '/gallery_teja_2.jpg', title: 'Teja S17', subtitle: 'Export Quality' },
    { url: '/gallery_byadgi_2.jpg', title: 'Byadgi', subtitle: 'Rich Color' },
    { url: '/gallery_shark_2.jpg', title: 'Shark S21', subtitle: 'High Pungency' },
    { url: '/gallery_packaging_1.jpg', title: 'Quality Packaging', subtitle: 'Export Ready' },
    { url: '/gallery_teja_3.jpg', title: 'Teja S17', subtitle: 'Whole Pods' },
    { url: '/gallery_endo_2.jpg', title: 'Endo 5', subtitle: 'Blood Red Color' }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % galleryImages.length);
    }, 3000); // Change image every 3 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative py-12 md:py-24 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold text-[#2C2C2C] mb-4 tracking-tight">
            our premium range
          </h2>
          <p className="text-base md:text-xl text-gray-600 font-light">authentic chillies from India's finest regions</p>
        </div>

        <div className="relative max-w-6xl mx-auto">
          {/* Main rotating image */}
          <div className="relative h-[280px] sm:h-[380px] md:h-[500px] rounded-2xl md:rounded-[3rem] overflow-hidden shadow-2xl">
            {galleryImages.map((image, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-all duration-1000 ${
                  index === currentIndex
                    ? 'opacity-100 scale-100'
                    : 'opacity-0 scale-95'
                }`}
              >
                <img
                  src={image.url}
                  alt={image.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                
                {/* Image title overlay */}
                <div className="absolute bottom-6 left-6 md:bottom-12 md:left-12 text-white z-10">
                  <h3 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-1 md:mb-2">{image.title}</h3>
                  <p className="text-sm md:text-xl font-light text-white/90">{image.subtitle}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Thumbnail navigation - hidden on mobile */}
          <div className="hidden md:flex justify-center gap-4 mt-8 flex-wrap">
            {galleryImages.map((image, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`relative w-20 h-20 rounded-xl overflow-hidden transition-all duration-300 ${
                  index === currentIndex
                    ? 'ring-4 ring-[#8B0000] scale-110'
                    : 'opacity-50 hover:opacity-100'
                }`}
              >
                <img
                  src={image.url}
                  alt={image.title}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>

          {/* Progress indicators */}
          <div className="flex justify-center gap-1.5 md:gap-2 mt-4 md:mt-6 flex-wrap">
            {galleryImages.map((_, index) => (
              <div
                key={index}
                className={`h-1 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'w-12 bg-[#8B0000]'
                    : 'w-8 bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Grid of all images with subtle animation */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6 mt-12 md:mt-20 max-w-6xl mx-auto">
          {[
            { url: '/gallery_teja_4.jpg', title: 'Teja S17', subtitle: 'Premium Quality' },
            { url: '/gallery_byadgi_3.jpg', title: 'Byadgi', subtitle: 'Dark Red' },
            { url: '/gallery_shark_3.jpg', title: 'Shark S21', subtitle: 'Export Grade' },
            { url: '/gallery_packaging_2.jpg', title: 'Premium Packaging', subtitle: 'Food Grade' },
            { url: '/gallery_teja_1.jpg', title: 'Teja S17', subtitle: 'Whole Pods' },
            { url: '/gallery_armour_1.jpg', title: 'Armour S18', subtitle: 'Thick Skin' },
            { url: '/gallery_sanam_1.jpg', title: 'Sanam S334', subtitle: 'Bright Red' },
            { url: '/gallery_endo_2.jpg', title: 'Endo 5', subtitle: 'Blood Red' }
          ].map((image, index) => (
            <div
              key={index}
              className="relative group overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 aspect-square"
              style={{
                animationDelay: `${index * 0.1}s`,
                animation: 'fadeIn 0.6s ease-out forwards'
              }}
            >
              <img
                src={image.url}
                alt={image.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-4 left-4 text-white">
                  <h4 className="text-lg font-bold">{image.title}</h4>
                  <p className="text-sm font-light">{image.subtitle}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Form Component with backend integration
const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', product_interest: '', message: '', phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      
      if (!res.ok) throw new Error('Failed to submit');
      
      setSubmitStatus('success');
      setFormData({ name: '', email: '', product_interest: '', message: '', phone: '' });
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="md:col-span-3 border-none shadow-2xl">
      <CardContent className="p-6 md:p-10">
        {submitStatus === 'success' ? (
          <div className="text-center py-10 space-y-4" data-testid="contact-form-success">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-[#2C2C2C]">Inquiry Sent!</h3>
            <p className="text-gray-600 font-light">We'll get back to you shortly at your email address.</p>
            <Button 
              onClick={() => setSubmitStatus(null)} 
              variant="outline"
              className="mt-4 rounded-xl"
              data-testid="send-another-btn"
            >
              Send Another Inquiry
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 md:mb-3">Name *</label>
                <input 
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="contact-name-input"
                  className="w-full px-4 md:px-5 py-3 md:py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#8B0000] focus:border-transparent transition-all text-gray-900 font-light text-sm md:text-base"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 md:mb-3">Email *</label>
                <input 
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="contact-email-input"
                  className="w-full px-4 md:px-5 py-3 md:py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#8B0000] focus:border-transparent transition-all text-gray-900 font-light text-sm md:text-base"
                  placeholder="your@email.com"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 md:mb-3">Phone</label>
                <input 
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  data-testid="contact-phone-input"
                  className="w-full px-4 md:px-5 py-3 md:py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#8B0000] focus:border-transparent transition-all text-gray-900 font-light text-sm md:text-base"
                  placeholder="+91-XXX-XXX-XXXX"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 md:mb-3">Product Interest</label>
                <select 
                  name="product_interest"
                  value={formData.product_interest}
                  onChange={handleChange}
                  data-testid="contact-product-select"
                  className="w-full px-4 md:px-5 py-3 md:py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#8B0000] focus:border-transparent transition-all text-gray-900 font-light text-sm md:text-base"
                >
                  <option value="">Select Product</option>
                  {mockData.products.map(p => (
                    <option key={p.id} value={p.name}>{p.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 md:mb-3">Message *</label>
              <textarea 
                rows={4}
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                data-testid="contact-message-input"
                className="w-full px-4 md:px-5 py-3 md:py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#8B0000] focus:border-transparent transition-all text-gray-900 font-light text-sm md:text-base"
                placeholder="Tell us about your requirements..."
              />
            </div>
            {submitStatus === 'error' && (
              <div className="text-red-600 text-sm bg-red-50 px-4 py-3 rounded-xl" data-testid="contact-form-error">
                Something went wrong. Please try again or email us directly.
              </div>
            )}
            <Button 
              type="submit"
              disabled={isSubmitting}
              data-testid="contact-submit-btn"
              className="w-full bg-[#8B0000] hover:bg-[#6B0000] text-white py-6 md:py-7 text-base md:text-lg font-semibold transition-all duration-500 rounded-xl shadow-xl hover:shadow-2xl group disabled:opacity-70"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  Send Inquiry
                  <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
};


const Home = () => {
  const [animatedStats, setAnimatedStats] = useState({
    years: 0,
    countries: 0,
    clients: 0,
    capacity: 0
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState({});
  const observerRefs = useRef([]);

  useEffect(() => {
    // Animate stats
    const duration = 2500;
    const steps = 60;
    const interval = duration / steps;

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = Math.min(step / steps, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      
      setAnimatedStats({
        years: Math.floor(mockData.stats.years * easeOut),
        countries: Math.floor(mockData.stats.countries * easeOut),
        clients: Math.floor(mockData.stats.clients * easeOut),
        capacity: Math.floor(mockData.stats.capacity * easeOut)
      });

      if (step >= steps) clearInterval(timer);
    }, interval);

    // Scroll handling
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);

    // Intersection Observer for animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '-50px' }
    );

    observerRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      clearInterval(timer);
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  const scrollToContact = () => {
    document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
  };

  const getIcon = (iconName) => {
    const icons = {
      Leaf, Award, Truck, TrendingUp, Package, Users, Shield, Clock,
      Sparkles, Zap, Building2
    };
    const IconComponent = icons[iconName] || Award;
    return <IconComponent className="w-12 h-12" />;
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Header - Premium sticky nav */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrollY > 50 
            ? 'bg-white/98 backdrop-blur-xl shadow-lg border-b border-gray-100' 
            : 'bg-white'
        }`}
      >
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_spiceworld-1/artifacts/pnkr92rc_Website_Logo_Transparent.png" 
                alt="Risorsa Exim - Premium Spice Exporter Since 1984" 
                className={`transition-all duration-300 ${scrollY > 50 ? 'h-10' : 'h-12'} w-auto`}
              />
            </div>
            <nav className="hidden md:flex items-center space-x-10">
              <a href="#home" className="text-sm font-medium text-gray-700 hover:text-[#8B0000] transition-all duration-300 relative group">
                Home
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#8B0000] group-hover:w-full transition-all duration-300"></span>
              </a>
              <a href="#products" className="text-sm font-medium text-gray-700 hover:text-[#8B0000] transition-all duration-300 relative group">
                Products
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#8B0000] group-hover:w-full transition-all duration-300"></span>
              </a>
              <a href="#about" className="text-sm font-medium text-gray-700 hover:text-[#8B0000] transition-all duration-300 relative group">
                About
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#8B0000] group-hover:w-full transition-all duration-300"></span>
              </a>
              <a href="#contact" className="text-sm font-medium text-gray-700 hover:text-[#8B0000] transition-all duration-300 relative group">
                Contact
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#8B0000] group-hover:w-full transition-all duration-300"></span>
              </a>
              <Button 
                onClick={scrollToContact}
                className="bg-[#8B0000] hover:bg-[#6B0000] text-white px-8 py-3 text-sm font-medium transition-all duration-500 hover:scale-105 hover:shadow-2xl rounded-full"
              >
                Request Quote
              </Button>
            </nav>
            <Button 
              variant="ghost" 
              className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-all"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pt-6 pb-4 space-y-4 animate-in slide-in-from-top">
              {['home', 'products', 'about', 'contact'].map((item) => (
                <a 
                  key={item}
                  href={`#${item}`} 
                  className="block text-gray-700 hover:text-[#8B0000] transition-colors font-medium capitalize py-2" 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item}
                </a>
              ))}
              <Button 
                onClick={() => { scrollToContact(); setMobileMenuOpen(false); }}
                className="w-full bg-[#8B0000] hover:bg-[#6B0000] text-white rounded-full py-6"
              >
                Request Quote
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section - Ultra modern with parallax effect */}
      <section id="home" className="relative pt-28 pb-16 md:pt-44 md:pb-32 min-h-[80vh] md:min-h-screen flex items-center overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-bl from-[#8B0000]/5 to-transparent pointer-events-none"></div>
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-center">
            <div className="space-y-6 md:space-y-10">
              <div className="space-y-4 md:space-y-6">
                <Badge className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#8B0000] border-none text-xs md:text-sm px-4 md:px-6 py-2 md:py-3 font-semibold tracking-wide shadow-lg">
                  EST. 1984 &#8226; NAGPUR & GUNTUR
                </Badge>
                <h1 className="text-5xl sm:text-6xl md:text-8xl lg:text-9xl font-bold text-[#2C2C2C] leading-[0.95] tracking-tighter">
                  premium
                  <br />
                  <span className="text-[#8B0000]">quality</span>
                  <br />
                  export
                </h1>
                <p className="text-lg sm:text-xl md:text-3xl text-gray-600 font-light leading-relaxed max-w-xl">
                  40+ years of trading heritage in dried red chillies
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 md:gap-5">
                <Button 
                  size="lg" 
                  onClick={scrollToContact}
                  className="bg-[#8B0000] hover:bg-[#6B0000] text-white px-8 md:px-12 py-6 md:py-8 text-base md:text-lg font-semibold transition-all duration-500 hover:scale-105 shadow-2xl hover:shadow-[#8B0000]/30 rounded-full group"
                >
                  Get Quote 
                  <ArrowRight className="ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => document.getElementById('products').scrollIntoView({ behavior: 'smooth' })}
                  className="border-2 border-[#2C2C2C] text-[#2C2C2C] hover:bg-[#2C2C2C] hover:text-white px-8 md:px-12 py-6 md:py-8 text-base md:text-lg font-semibold transition-all duration-500 rounded-full"
                >
                  View Products
                </Button>
              </div>

              {/* Quick stats inline */}
              <div className="flex items-center gap-4 md:gap-8 pt-4">
                <div className="text-center">
                  <div className="text-2xl md:text-4xl font-bold text-[#8B0000]">40+</div>
                  <div className="text-[10px] md:text-xs text-gray-500 font-medium tracking-wide mt-1">YEARS</div>
                </div>
                <div className="h-8 md:h-12 w-px bg-gray-300"></div>
                <div className="text-center">
                  <div className="text-2xl md:text-4xl font-bold text-[#8B0000]">50+</div>
                  <div className="text-[10px] md:text-xs text-gray-500 font-medium tracking-wide mt-1">COUNTRIES</div>
                </div>
                <div className="h-8 md:h-12 w-px bg-gray-300"></div>
                <div className="text-center">
                  <div className="text-2xl md:text-4xl font-bold text-[#8B0000]">ISO</div>
                  <div className="text-[10px] md:text-xs text-gray-500 font-medium tracking-wide mt-1">CERTIFIED</div>
                </div>
              </div>
            </div>
            
            <div className="relative hidden md:block">
              <div 
                className="relative rounded-[3rem] overflow-hidden shadow-[0_30px_70px_-15px_rgba(0,0,0,0.3)] transform hover:scale-[1.02] transition-all duration-700"
                style={{ transform: `translateY(${scrollY * 0.1}px)` }}
              >
                <img 
                  src="https://static.wixstatic.com/media/33f236_a44f7edcebac44e880cd968e99b21498~mv2.jpeg/v1/fill/w_1200,h_1200,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/33f236_a44f7edcebac44e880cd968e99b21498~mv2.jpeg"
                  alt="Premium Export Quality Indian Chillies - Risorsa Exim"
                  className="w-full h-[500px] lg:h-[650px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                
                {/* Floating badge */}
                <div className="absolute bottom-8 left-8 bg-white/95 backdrop-blur-xl rounded-2xl p-6 shadow-2xl">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-[#8B0000] rounded-full flex items-center justify-center">
                      <Award className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-[#2C2C2C]">FSSAI</div>
                      <div className="text-sm text-gray-600">Certified Quality</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 hidden md:flex flex-col items-center gap-2 animate-bounce">
          <span className="text-xs text-gray-400 font-medium tracking-wider">SCROLL</span>
          <ChevronDown className="w-5 h-5 text-gray-400" />
        </div>
      </section>

      {/* Marquee Section - Company Highlights */}
      <section className="py-4 md:py-6 bg-[#2C2C2C] overflow-hidden">
        <div className="flex items-center gap-6 md:gap-12 animate-marquee whitespace-nowrap">
          {[...Array(3)].map((_, i) => (
            <React.Fragment key={i}>
              <span className="text-white/80 text-xs md:text-sm font-medium tracking-wider">&#9733; ISO CERTIFIED</span>
              <span className="text-white/80 text-xs md:text-sm font-medium tracking-wider">&#9733; FSSAI APPROVED</span>
              <span className="text-white/80 text-xs md:text-sm font-medium tracking-wider">&#9733; EXPORT QUALITY</span>
              <span className="text-white/80 text-xs md:text-sm font-medium tracking-wider">&#9733; 40+ YEARS HERITAGE</span>
              <span className="text-white/80 text-xs md:text-sm font-medium tracking-wider">&#9733; GUNTUR & NAGPUR</span>
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* Animated Product Gallery - Rotating Images */}
      <ImageGallerySection />

      {/* Trust Indicators - Minimalist cards */}
      <section className="py-16 md:py-32 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-[#2C2C2C] mb-4 md:mb-6 tracking-tight">
              quality assurance
            </h2>
            <p className="text-base md:text-xl text-gray-600 font-light max-w-2xl mx-auto">
              stringent testing & international standards
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-6xl mx-auto">
            {[
              { icon: Shield, title: 'ISO Certified', desc: 'International Standards' },
              { icon: Award, title: 'FSSAI', desc: 'Export Ready' },
              { icon: CheckCircle, title: 'Lab Tested', desc: 'Quality Verified' },
              { icon: Package, title: 'Traceability', desc: 'Full Documentation' }
            ].map((item, i) => (
              <Card key={i} className="border-none shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 bg-white">
                <CardContent className="p-5 md:p-10 text-center space-y-3 md:space-y-4">
                  <div className="w-14 h-14 md:w-20 md:h-20 bg-[#8B0000]/10 rounded-full flex items-center justify-center mx-auto">
                    <item.icon className="w-7 h-7 md:w-10 md:h-10 text-[#8B0000]" />
                  </div>
                  <h3 className="text-lg md:text-2xl font-bold text-[#2C2C2C]">{item.title}</h3>
                  <p className="text-sm md:text-base text-gray-600 font-light">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section - Premium grid with better images */}
      <section id="products" className="py-16 md:py-32 bg-white" ref={(el) => (observerRefs.current[0] = el)}>
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-12 md:mb-24">
            <h2 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-[#2C2C2C] mb-4 md:mb-8 tracking-tight">
              our products
            </h2>
            <p className="text-lg md:text-2xl text-gray-600 font-light max-w-3xl">
              Premium dried red chillies from Guntur & Nagpur<br/>
              <span className="text-sm md:text-lg text-gray-500">Available in whole, stemless, stemcut & powdered forms</span>
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {mockData.products.map((product, index) => (
              <Card 
                key={product.id} 
                className={`group border-none shadow-xl hover:shadow-[0_20px_60px_-15px_rgba(139,0,0,0.3)] transition-all duration-700 hover:-translate-y-4 overflow-hidden ${
                  isVisible[observerRefs.current[0]?.id] ? 'animate-in' : 'opacity-0'
                }`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="relative overflow-hidden h-52 sm:h-64 md:h-80">
                  <img 
                    src={product.image} 
                    alt={`${product.name} - ${product.subtitle} - Export Quality Chillies`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <Badge className="absolute top-6 right-6 bg-white/95 backdrop-blur-sm text-[#8B0000] font-bold shadow-lg border-none px-4 py-2">
                    {product.heatLevel}
                  </Badge>
                </div>
                <CardContent className="p-5 md:p-8 space-y-4 md:space-y-5">
                  <div>
                    <h3 className="text-xl md:text-3xl font-bold text-[#2C2C2C] mb-1 md:mb-2">{product.name}</h3>
                    <p className="text-xs md:text-sm text-[#8B0000] font-semibold tracking-wide uppercase">{product.subtitle}</p>
                  </div>
                  <p className="text-gray-600 text-sm font-light leading-relaxed">{product.description}</p>
                  <div className="space-y-3 pt-3 border-t border-gray-100">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 font-light">Color:</span>
                      <span className="font-semibold text-[#2C2C2C]">{product.color} ASTA</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 font-light">Moisture:</span>
                      <span className="font-semibold text-[#2C2C2C]">{product.moisture}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 font-light">Origin:</span>
                      <span className="font-semibold text-[#2C2C2C]">{product.origin}</span>
                    </div>
                  </div>
                  <Button 
                    onClick={scrollToContact}
                    data-testid={`request-sample-btn-${product.id}`}
                    className="w-full bg-[#2C2C2C] hover:bg-[#8B0000] text-white transition-all duration-500 mt-4 md:mt-6 py-5 md:py-6 rounded-xl font-semibold group"
                  >
                    Request Sample
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Processing Formats - Icon grid */}
      <section className="py-16 md:py-32 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold text-[#2C2C2C] mb-4 md:mb-6 tracking-tight">processing formats</h2>
            <p className="text-base md:text-xl text-gray-600 font-light">flexible options for every requirement</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8 max-w-6xl mx-auto">
            {mockData.processingFormats.map((format, i) => (
              <Card key={format.id} className="border-none shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 group">
                <CardContent className="p-5 md:p-8 text-center space-y-3 md:space-y-4">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-[#8B0000] to-[#6B0000] rounded-xl md:rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-500 shadow-xl">
                    {getIcon(format.icon)}
                    <div className="absolute w-16 h-16 bg-[#8B0000] rounded-2xl opacity-0 group-hover:opacity-20 animate-ping"></div>
                  </div>
                  <h3 className="text-lg md:text-2xl font-bold text-[#2C2C2C]">{format.title}</h3>
                  <p className="text-gray-600 font-light text-xs md:text-sm leading-relaxed">{format.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Packaging Solutions */}
      <section className="py-16 md:py-32 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold text-[#2C2C2C] mb-4 md:mb-6 tracking-tight">flexible packaging</h2>
            <p className="text-base md:text-xl text-gray-600 font-light">tailored solutions for every requirement</p>
          </div>
          
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6 md:gap-12 mb-10 md:mb-16">
              <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-500 p-5 md:p-10">
                <CardContent className="space-y-6 p-0">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 bg-[#8B0000]/10 rounded-xl flex items-center justify-center">
                      <Package className="w-7 h-7 text-[#8B0000]" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#2C2C2C]">Standard Sizes</h3>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    {['5 kg', '10 kg', '15 kg', '20 kg', '25 kg', '40 kg'].map((size, i) => (
                      <div key={i} className="bg-gray-50 rounded-xl p-4 text-center hover:bg-[#8B0000] hover:text-white transition-all duration-300 cursor-pointer group">
                        <div className="text-2xl font-bold text-[#8B0000] group-hover:text-white">{size}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-none shadow-xl hover:shadow-2xl transition-all duration-500 p-5 md:p-10">
                <CardContent className="space-y-4 md:space-y-6 p-0">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 bg-[#8B0000]/10 rounded-xl flex items-center justify-center">
                      <Package className="w-7 h-7 text-[#8B0000]" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#2C2C2C]">Packaging Materials</h3>
                  </div>
                  <div className="space-y-4">
                    {['Carton Boxes', 'Jute Bags', 'PP Bags (with/without holes)', 'Custom Options Available'].map((material, i) => (
                      <div key={i} className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-[#8B0000]/5 transition-all duration-300">
                        <CheckCircle className="w-5 h-5 text-[#8B0000] flex-shrink-0" />
                        <span className="text-gray-700 font-light">{material}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <div className="bg-gradient-to-r from-[#8B0000] to-[#6B0000] rounded-2xl md:rounded-3xl p-6 md:p-10 text-white text-center">
              <Shield className="w-10 h-10 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 opacity-90" />
              <h4 className="text-xl md:text-2xl font-bold mb-2 md:mb-3">Food Grade Certified</h4>
              <p className="text-white/90 font-light text-sm md:text-lg">All packaging meets international food safety standards</p>
            </div>
          </div>
        </div>
      </section>

      {/* Order Process */}
      <section className="py-16 md:py-32 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold text-[#2C2C2C] mb-4 md:mb-6 tracking-tight">order process</h2>
            <p className="text-base md:text-xl text-gray-600 font-light">seamless journey from inquiry to delivery</p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-8">
              {[
                { step: '01', title: 'Order Received', desc: 'Sales contract prepared & PO confirmed', icon: CheckCircle },
                { step: '02', title: 'Advance Payment', desc: 'Payment received & production starts', icon: TrendingUp },
                { step: '03', title: 'Processing', desc: 'Regular updates with photos & videos', icon: Package },
                { step: '04', title: 'Quality Check', desc: 'Processing complete & spec sheet shared', icon: Shield },
                { step: '05', title: 'Loading', desc: 'Container loading begins', icon: Truck },
                { step: '06', title: 'Documentation', desc: 'All documents sent via email', icon: CheckCircle }
              ].map((item, i) => (
                <Card key={i} className="border-none shadow-lg hover:shadow-2xl transition-all duration-500 relative overflow-hidden group">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#8B0000] to-[#D4AF37] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                  <CardContent className="p-5 md:p-8 space-y-3 md:space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="text-4xl md:text-6xl font-bold text-gray-100 group-hover:text-[#8B0000]/10 transition-colors">
                        {item.step}
                      </div>
                      <div className="w-12 h-12 bg-[#8B0000]/10 rounded-xl flex items-center justify-center group-hover:bg-[#8B0000] transition-all duration-500">
                        <item.icon className="w-6 h-6 text-[#8B0000] group-hover:text-white transition-colors" />
                      </div>
                    </div>
                    <h3 className="text-lg md:text-2xl font-bold text-[#2C2C2C]">{item.title}</h3>
                    <p className="text-sm md:text-base text-gray-600 font-light leading-relaxed">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            <div className="mt-10 md:mt-16 text-center">
              <div className="inline-flex items-center gap-3 bg-white rounded-xl md:rounded-2xl px-5 md:px-8 py-4 md:py-5 shadow-xl">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <div className="text-left">
                  <div className="text-sm text-gray-500 font-light">Average Timeline</div>
                  <div className="text-2xl font-bold text-[#2C2C2C]">Complete Documentation</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-32 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-10 md:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-[#2C2C2C] mb-4 md:mb-8 tracking-tight">
              why risorsa exim
            </h2>
            <p className="text-base md:text-xl text-gray-600 font-light">excellence in every aspect</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
            {mockData.whyChooseUs.map((item, i) => (
              <Card key={item.id} className="border-none shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 group">
                <CardContent className="p-6 md:p-10 space-y-3 md:space-y-5">
                  <div className="w-16 h-16 bg-[#8B0000]/10 rounded-xl flex items-center justify-center group-hover:bg-[#8B0000] transition-colors duration-500">
                    <div className="text-[#8B0000] group-hover:text-white transition-colors duration-500">
                      {getIcon(item.icon)}
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-[#2C2C2C]">{item.title}</h3>
                  <p className="text-gray-600 font-light leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 md:py-32 bg-[#F5F5DC]">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-10 md:gap-20 items-center">
            <div className="space-y-6 md:space-y-10">
              <div>
                <Badge className="mb-4 md:mb-8 bg-[#8B0000] text-white px-4 md:px-6 py-2 md:py-3 text-xs md:text-sm font-semibold tracking-wide shadow-lg">
                  SINCE 1984
                </Badge>
                <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold text-[#2C2C2C] mb-4 md:mb-8 tracking-tight">
                  40+ years of
                  <br />
                  trading heritage
                </h2>
                <p className="text-base md:text-xl text-gray-700 font-light leading-relaxed">
                  One of India's leading traders of premium dried red chillies with unwavering commitment to quality and long-term buyer relationships.
                </p>
              </div>
              
              <div className="space-y-6">
                {[
                  { title: 'Head Office', location: 'Nagpur, Maharashtra', role: 'Operations & Trading' },
                  { title: 'Branch Office', location: 'Guntur, Andhra Pradesh', role: 'Sourcing & Processing' }
                ].map((office, i) => (
                  <div key={i} className="flex items-start gap-5 p-6 bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-500">
                    <div className="w-12 h-12 bg-[#8B0000]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-6 h-6 text-[#8B0000]" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[#2C2C2C] text-lg mb-1">{office.title}</h4>
                      <p className="text-gray-600 font-medium">{office.location}</p>
                      <p className="text-sm text-gray-500 font-light mt-1">{office.role}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-4 pt-4">
                {[
                  'Stringent quality tests at every stage',
                  'Personal attention to product quality',
                  'Complete supply chain traceability',
                  'Long-term buyer relationships'
                ].map((point, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#8B0000] mt-0.5 flex-shrink-0" />
                    <p className="text-gray-700 font-light">{point}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative hidden md:block">
              <div className="rounded-[3rem] overflow-hidden shadow-[0_30px_70px_-15px_rgba(0,0,0,0.3)]">
                <img 
                  src="https://static.wixstatic.com/media/33f236_d968559ce53f4f39ab6947d1c015e646~mv2.jpg/v1/fill/w_1200,h_1200,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/33f236_d968559ce53f4f39ab6947d1c015e646~mv2.jpg"
                  alt="Risorsa Exim Premium Chilli Processing Facilities"
                  className="w-full h-[500px] lg:h-[700px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 md:py-32 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-10 md:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-[#2C2C2C] mb-4 md:mb-8 tracking-tight">let's partner</h2>
            <p className="text-lg md:text-2xl text-gray-600 font-light">premium quality &#8226; reliable supply &#8226; global standards</p>
          </div>
          
          <div className="grid md:grid-cols-5 gap-8 md:gap-16 max-w-6xl mx-auto">
            <div className="md:col-span-2 space-y-6 md:space-y-10">
              {[
                { icon: MapPin, title: 'Address', info: 'SHO NO.5, OCT HALL\n3 APMC CHILLIES MARKET' },
                { icon: Phone, title: 'Phone', info: '+91-880-668-8886', link: 'tel:+918806688886' },
                { icon: Mail, title: 'Email', info: 'support@risorsaeximpvtltd.com', link: 'mailto:support@risorsaeximpvtltd.com' }
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 md:gap-5">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-[#8B0000]/10 rounded-xl md:rounded-2xl flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 md:w-7 md:h-7 text-[#8B0000]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#2C2C2C] text-base md:text-lg mb-1 md:mb-2">{item.title}</h3>
                    {item.link ? (
                      <a href={item.link} className="text-sm md:text-base text-gray-600 hover:text-[#8B0000] transition-colors font-light whitespace-pre-line break-all">
                        {item.info}
                      </a>
                    ) : (
                      <p className="text-sm md:text-base text-gray-600 font-light whitespace-pre-line">{item.info}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <ContactForm />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] text-white py-12 md:py-20">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 mb-10 md:mb-16">
            <div className="space-y-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_spiceworld-1/artifacts/pnkr92rc_Website_Logo_Transparent.png" 
                alt="Risorsa Exim Logo" 
                className="h-14 w-auto brightness-0 invert"
              />
              <p className="text-gray-400 font-light leading-relaxed">
                Premium quality dried red chillies since 1984
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-6 text-white text-lg">Quick Links</h4>
              <ul className="space-y-4">
                {['Home', 'Products', 'About', 'Contact'].map((link) => (
                  <li key={link}>
                    <a href={`#${link.toLowerCase()}`} className="text-gray-400 hover:text-white transition-colors font-light">
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-6 text-white text-lg">Products</h4>
              <ul className="space-y-4">
                {mockData.products.slice(0, 4).map(p => (
                  <li key={p.id} className="text-gray-400 font-light">{p.name}</li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-6 text-white text-lg">Connect</h4>
              <div className="space-y-4 text-gray-400 font-light">
                <p>Nagpur, Maharashtra</p>
                <p>Guntur, Andhra Pradesh</p>
                <p>+91-880-668-8886</p>
                <p>support@risorsaeximpvtltd.com</p>
              </div>
            </div>
          </div>
          <Separator className="my-12 bg-gray-800" />
          <div className="text-center text-gray-500 font-light">
            <p>&copy; 2025 RISORSA EXIM PVT LTD. All rights reserved. | ISO Certified | FSSAI Approved</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/919309015506?text=Hi%2C%20I%27m%20interested%20in%20your%20premium%20chillies.%20Can%20you%20share%20more%20details%3F"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-float-btn"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 md:w-16 md:h-16 bg-[#25D366] rounded-full flex items-center justify-center shadow-[0_4px_20px_rgba(37,211,102,0.5)] hover:scale-110 hover:shadow-[0_6px_28px_rgba(37,211,102,0.6)] transition-all duration-300 group"
        aria-label="Chat on WhatsApp"
      >
        <svg className="w-7 h-7 md:w-8 md:h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span className="absolute right-full mr-3 bg-white text-gray-800 text-sm font-medium px-4 py-2 rounded-xl shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none hidden md:block">
          Chat with us
        </span>
      </a>
    </div>
  );
};

export default Home;
